/**
 * Herramientas Administrativas: respaldo y restauración JSON.
 * Visible solo para pastor/pastora/administrador; requiere contraseña al entrar.
 */
import { useState } from "react";
import { Lock, LogOut, Wrench } from "lucide-react";
import { api } from "@/lib/apiClient";
import {
  clearAdminToolsUnlock,
  getAdminToolsUnlockToken,
  setAdminToolsUnlockToken,
} from "@/lib/roles";
import { DataIoToolbar } from "@/components/DataIoToolbar";
import { SystemAuditSection } from "@/components/SystemAuditSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** Ruta canónica (coincide con backend /api/import-export/admin-tools/unlock). */
export const ADMIN_TOOLS_UNLOCK_PATH = "/import-export/admin-tools/unlock";

function unlockErrorMessage(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) {
    const detail = d.trim();
    if (detail === "Not Found" || detail === "not found") {
      return "No se encontró el servicio de desbloqueo. Reinicie el backend y vuelva a intentar.";
    }
    if (/contrase[nñ]a incorrecta/i.test(detail)) return "Contraseña incorrecta";
    return detail;
  }
  if (status === 401) return "Contraseña incorrecta";
  if (status === 403) {
    return "No autorizado. Solo Administrador, Pastor o Pastora pueden acceder.";
  }
  if (status === 404) {
    return "No se encontró el servicio de desbloqueo. Reinicie el backend y vuelva a intentar.";
  }
  return err?.message || "No se pudo desbloquear. Intente de nuevo.";
}

/**
 * @param {{ onCompleted?: () => void, onExit?: () => void }} props
 */
export function AdminToolsPanel({ onCompleted, onExit }) {
  const [unlocked, setUnlocked] = useState(() => Boolean(getAdminToolsUnlockToken()));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.post(ADMIN_TOOLS_UNLOCK_PATH, { password: String(password || "") });
      const token = res?.data?.unlock_token;
      if (!token) {
        setError("No se recibió el token de desbloqueo.");
        return;
      }
      setAdminToolsUnlockToken(token);
      setUnlocked(true);
      setPassword("");
    } catch (err) {
      setAdminToolsUnlockToken("");
      setUnlocked(false);
      setError(unlockErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleExitAdminTools = () => {
    clearAdminToolsUnlock();
    setAdminToolsUnlockToken("");
    setUnlocked(false);
    setPassword("");
    setError("");
    onExit?.();
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
              <Wrench className="w-5 h-5" />
              Herramientas Administrativas
            </CardTitle>
            <CardDescription>
              Ingrese la contraseña para continuar. Al salir de esta sección deberá volver a ingresarla.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUnlock} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-tools-password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden />
                  <Input
                    id="admin-tools-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    placeholder="Contraseña de acceso"
                    disabled={busy}
                    required
                  />
                </div>
              </div>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy || !password.trim()}>
                {busy ? "Verificando…" : "Desbloquear"}
              </Button>
            </form>
          </CardContent>
        </Card>
        {typeof onExit === "function" ? (
          <Button type="button" variant="outline" className="w-full" onClick={handleExitAdminTools}>
            <LogOut className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            Herramientas Administrativas
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Respaldo y restauración de datos de la iglesia. Use estas opciones con cuidado.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleExitAdminTools}
          className="border-slate-300 text-slate-800 hover:bg-slate-50 shrink-0"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Salir de Herramientas Administrativas
        </Button>
      </div>
      <DataIoToolbar resource="backup" canRestore onCompleted={onCompleted} />
      <SystemAuditSection />
    </div>
  );
}
